// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import {
  QuarantineResolutionKind,
  Shipment,
  shipmentSchema,
} from '@/shipment/api/types';
import { z } from 'zod';

/**
 * Schema for validating the response from the resolve quarantined serial endpoint.
 */
const resolveQuarantinedSerialResponseSchema = z.object({
  data: shipmentSchema,
  metadata: responseMetadataSchema,
});

export interface ResolveQuarantinedSerialRequest {
  kind: QuarantineResolutionKind;
  // Required justification for the resolution.
  note: string;
  // The serial that actually shipped; required for a corrected-serial resolution.
  correctedSerial?: string;
}

/**
 * Resolves a held serial on a shipment. The kind decides the effect: keep
 * Hardfin's item, or correct the serial to another asset of the reported item.
 *
 * @param shipmentKey - The key of the shipment holding the serial
 * @param quarantineKey - The key of the held serial to resolve
 * @param request - The resolution details
 * @returns The updated shipment
 */
export async function resolveQuarantinedSerial(
  shipmentKey: string,
  quarantineKey: string,
  request: ResolveQuarantinedSerialRequest
): Promise<Shipment> {
  const url = `/v1/shipment/${shipmentKey}/quarantine/${quarantineKey}/resolve`;
  const response = await api.post(url, request);

  const { data } = await parseResponse(
    resolveQuarantinedSerialResponseSchema,
    response.data,
    {
      schemaName: 'ResolveQuarantinedSerial',
      endpoint: url,
    }
  );
  return data;
}
